import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { useClinic } from "./context/ClinicContext";
import ConfirmDialog from "./components/ConfirmDialog";
import {
  Sparkles,
  Send,
  Bot,
  UserRound,
  CalendarDays,
  UsersRound,
  Lightbulb,
  ShieldCheck,
  MessageSquare,
  Plus,
  X,
  Check,
  Trash2,
  Mic,
  MicOff,
} from "lucide-react";

const sugerencias = [
  {
    icon: CalendarDays,
    texto: "¿Qué citas tengo hoy y en qué horarios libres puedo agendar?",
  },
  {
    icon: UsersRound,
    texto: "Registra un paciente nuevo",
  },
  {
    icon: Lightbulb,
    texto: "Registra un pago de un paciente",
  },
];

const llamarVexaDentAI = httpsCallable(
  functions,
  "vexaDentAI",
  { timeout: 300000 }
);

function crearConversationId() {
  return `vd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Interpreta negritas y cursivas sin insertar HTML.
function textoEnriquecido(texto, clave = "texto") {
  const partes = String(texto).split(
    /(\*\*[\s\S]+?\*\*|\*[^*\n]+?\*|`[^`\n]+?`)/g
  );

  return partes.map((parte, indice) => {
    const key = `${clave}-${indice}`;

    if (parte.startsWith("**") && parte.endsWith("**")) {
      return <strong key={key}>{parte.slice(2, -2)}</strong>;
    }

    if (parte.startsWith("*") && parte.endsWith("*")) {
      return <em key={key}>{parte.slice(1, -1)}</em>;
    }

    if (parte.startsWith("`") && parte.endsWith("`")) {
      return (
        <code key={key} style={styles.inlineCode}>
          {parte.slice(1, -1)}
        </code>
      );
    }

    return parte;
  });
}

// Renderiza títulos, párrafos y listas Markdown.
function ContenidoMarkdown({ contenido }) {
  const lineas = String(contenido || "").split(/\r?\n/);
  const elementos = [];
  let lista = [];
  let contador = 0;

  const vaciarLista = () => {
    if (!lista.length) return;

    elementos.push(
      <ul key={`lista-${contador++}`} style={styles.markdownList}>
        {lista.map((elemento, indice) => (
          <li key={`item-${indice}`}>
            {textoEnriquecido(elemento, `lista-${contador}-${indice}`)}
          </li>
        ))}
      </ul>
    );

    lista = [];
  };

  lineas.forEach((linea, indice) => {
    const texto = linea.trim();

    const elementoLista = texto.match(/^[-*•]\s+(.+)$/);

    if (elementoLista) {
      lista.push(elementoLista[1]);
      return;
    }

    vaciarLista();

    if (!texto) return;

    const titulo = texto.match(/^(#{1,3})\s+(.+)$/);

    if (titulo) {
      const nivel = titulo[1].length;
      const contenidoTitulo = textoEnriquecido(
        titulo[2],
        `titulo-${indice}`
      );

      elementos.push(
        nivel === 1 ? (
          <h3 key={indice} style={styles.markdownHeading}>
            {contenidoTitulo}
          </h3>
        ) : nivel === 2 ? (
          <h4 key={indice} style={styles.markdownSubheading}>
            {contenidoTitulo}
          </h4>
        ) : (
          <h5 key={indice} style={styles.markdownSmallHeading}>
            {contenidoTitulo}
          </h5>
        )
      );

      return;
    }

    elementos.push(
      <p key={indice} style={styles.markdownParagraph}>
        {textoEnriquecido(texto, `parrafo-${indice}`)}
      </p>
    );
  });

  vaciarLista();

  return <div style={styles.markdownContent}>{elementos}</div>;
}

export default function VexaDentAI({ mobile = false } = {}) {
  const { clinicId } = useClinic();

  const [mensaje, setMensaje] = useState("");
  const [mensajes, setMensajes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [conversaciones, setConversaciones] = useState([]);
  const [historialCargando, setHistorialCargando] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState("");
  const [conversacionCargando, setConversacionCargando] = useState(null);
  const [conversacionActiva, setConversacionActiva] = useState(null);
  const [pendingWhatsAppDraft, setPendingWhatsAppDraft] = useState(null);
  const [historialMovilAbierto, setHistorialMovilAbierto] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deletingConversationId, setDeletingConversationId] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [voicePreparing, setVoicePreparing] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const chatRef = useRef(null);
  const voiceModeRef = useRef(false);
  const voiceRecognitionRef = useRef(null);
  const voiceStartingRef = useRef(false);
  const voiceTranscriptRef = useRef("");
  const voiceCommandArmedRef = useRef(false);
  const voiceArmTimeoutRef = useRef(null);
  const voiceRestartTimeoutRef = useRef(null);
  const voiceSpeakingRef = useRef(false);
  const voiceLocalReadyRef = useRef(false);
  const voiceLanguagePackReadyRef = useRef(false);
  const voiceRecognitionLanguageRef = useRef("es-MX");
  const voiceGenerationRef = useRef(0);
  const enviandoRef = useRef(false);
  const enviarRef = useRef(null);
  const hablarRespuestaRef = useRef(null);
  const iniciarEscuchaVozRef = useRef(null);

  useEffect(() => () => {
    voiceModeRef.current = false;
    window.clearTimeout(voiceArmTimeoutRef.current);
    window.clearTimeout(voiceRestartTimeoutRef.current);
    voiceRecognitionRef.current?.stop();
    window.speechSynthesis?.cancel();
  }, []);

  useEffect(() => {
    let vigente = true;
    setMensajes([]);
    setConversacionActiva(null);
    setPendingWhatsAppDraft(null);
    setConversaciones([]);
    setErrorHistorial("");
    setConversationToDelete(null);

    if (!clinicId) {
      setHistorialCargando(false);
      return () => {
        vigente = false;
      };
    }

    setHistorialCargando(true);
    llamarVexaDentAI({ clinicId, operation: "list_conversations" })
      .then((resultado) => {
        if (vigente) {
          setConversaciones(resultado?.data?.conversations || []);
          setErrorHistorial("");
        }
      })
      .catch((err) => {
        console.error("Error al cargar el historial de VexaDent AI:", err);
        if (vigente) setErrorHistorial("No se pudo cargar el historial.");
      })
      .finally(() => {
        if (vigente) setHistorialCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [clinicId]);

  useEffect(() => {
    const chat = chatRef.current;

    if (chat) {
      chat.scrollTop = chat.scrollHeight;
    }
  }, [mensajes, enviando, error]);

  const actualizarResumenConversacion = (conversationId, content) => {
    setConversaciones((actuales) => {
      const existente = actuales.find((item) => item.id === conversationId);
      const title = existente?.title || content.replace(/\s+/g, " ").trim().slice(0, 80) || "Nueva conversación";
      const actualizada = {
        id: conversationId,
        title,
        preview: content.slice(0, 180),
        updatedAt: new Date().toISOString(),
        messageCount: Number(existente?.messageCount || 0) + 1,
      };
      return [actualizada, ...actuales.filter((item) => item.id !== conversationId)];
    });
  };

  const iniciarNuevaConversacion = () => {
    if (enviando) return;
    setConversacionActiva(null);
    setPendingWhatsAppDraft(null);
    setMensajes([]);
    setMensaje("");
    setError("");
    setHistorialMovilAbierto(false);
  };

  const abrirConversacion = async (conversationId) => {
    if (!clinicId || enviando || conversacionCargando) return;
    setConversacionCargando(conversationId);
    setPendingWhatsAppDraft(null);
    setError("");

    try {
      const resultado = await llamarVexaDentAI({
        clinicId,
        operation: "load_conversation",
        conversationId,
      });
      const conversation = resultado?.data?.conversation;
      const loadedMessages = resultado?.data?.messages;
      if (!conversation || !Array.isArray(loadedMessages)) {
        throw new Error("No se pudo cargar esa conversación.");
      }

      setConversacionActiva(conversation.id);
      setPendingWhatsAppDraft(resultado?.data?.pendingWhatsAppDraft || null);
      setMensajes(loadedMessages.map((item) => ({
        id: item.id,
        role: item.role,
        content: item.content,
      })));
      setHistorialMovilAbierto(false);
    } catch (err) {
      console.error("Error al abrir una conversación de VexaDent AI:", err);
      setError(err?.message || "No se pudo abrir esa conversación.");
    } finally {
      setConversacionCargando(null);
    }
  };

  const eliminarConversacion = async () => {
    const target = conversationToDelete;
    if (!clinicId || !target || enviando || conversacionCargando || deletingConversationId) return;

    setDeletingConversationId(target.id);
    setErrorHistorial("");
    try {
      const resultado = await llamarVexaDentAI({
        clinicId,
        operation: "delete_conversation",
        conversationId: target.id,
      });
      if (!resultado?.data?.deleted) {
        throw new Error("No se pudo eliminar la conversación.");
      }

      setConversaciones((actuales) => actuales.filter((item) => item.id !== target.id));
      if (conversacionActiva === target.id) {
        setConversacionActiva(null);
        setMensajes([]);
        setPendingWhatsAppDraft(null);
        setMensaje("");
        setError("");
      }
      setConversationToDelete(null);
      if (mobile) setHistorialMovilAbierto(false);
    } catch (err) {
      console.error("Error al eliminar una conversación de VexaDent AI:", err);
      setErrorHistorial(err?.message || "No se pudo eliminar la conversación.");
    } finally {
      setDeletingConversationId(null);
    }
  };

  const enviar = async (texto = mensaje, { voiceInput = false } = {}) => {
    const contenido = String(texto || "").trim();

    if (!contenido || enviando) return;

    if (!clinicId) {
      setError("No se pudo identificar el consultorio activo.");
      return;
    }

    if (contenido.length > 6000) {
      setError("El mensaje no puede superar los 6000 caracteres.");
      return;
    }

    const conversationId = conversacionActiva || crearConversationId();
    const id = `${Date.now()}-${mensajes.length}`;
    if (!conversacionActiva) setConversacionActiva(conversationId);

    setMensajes((actuales) => [
      ...actuales,
      {
        id: `${id}-user`,
        role: "user",
        content: contenido,
      },
    ]);
    actualizarResumenConversacion(conversationId, contenido);

    setMensaje("");
    setError("");
    enviandoRef.current = true;
    setEnviando(true);

    try {
      const resultado = await llamarVexaDentAI({
        clinicId,
        conversationId,
        mensaje: contenido,
        voiceInput,
        historial: mensajes.slice(-20).map(({ role, content: texto }) => ({
          role,
          content: texto,
        })),
      });

      const respuesta = resultado?.data?.respuesta;
      const savedConversationId = resultado?.data?.conversationId || conversationId;

      if (!respuesta || typeof respuesta !== "string") {
        throw new Error(
          "VexaDent AI no devolvió una respuesta válida."
        );
      }

      setMensajes((actuales) => [
        ...actuales,
        {
          id: `${id}-assistant`,
          role: "assistant",
          content: respuesta,
        },
      ]);
      setConversacionActiva(savedConversationId);
      setPendingWhatsAppDraft(resultado?.data?.pendingWhatsAppDraft || null);
      actualizarResumenConversacion(savedConversationId, respuesta);
      hablarRespuestaRef.current?.(respuesta);
    } catch (err) {
      console.error("Error al consultar VexaDent AI:", err);

      if (pendingWhatsAppDraft) {
        try {
          const estado = await llamarVexaDentAI({
            clinicId,
            operation: "load_conversation",
            conversationId,
          });
          setPendingWhatsAppDraft(estado?.data?.pendingWhatsAppDraft || null);
        } catch (refreshError) {
          console.error("Error al actualizar el estado del borrador de WhatsApp:", refreshError);
        }
      }

      let mensajeError =
        "No se pudo obtener una respuesta. Inténtalo de nuevo.";

      if (err?.code === "functions/unauthenticated") {
        mensajeError =
          "Tu sesión no está autenticada. Cierra sesión e inicia sesión nuevamente.";
      } else if (err?.code === "functions/permission-denied") {
        mensajeError =
          "Tu usuario no tiene acceso autorizado a este consultorio.";
      } else if (err?.code === "functions/invalid-argument") {
        mensajeError =
          err.message ||
          "Revisa el mensaje e inténtalo de nuevo.";
      } else if (err?.code === "functions/not-found") {
        mensajeError =
          "No se encontró la función de VexaDent AI en Firebase.";
      } else if (err?.code === "functions/internal") {
        mensajeError =
          err.message ||
          "Ocurrió un error al procesar la solicitud.";
      } else if (err?.message) {
        mensajeError = err.message;
      }

      setError(mensajeError);
      hablarRespuestaRef.current?.(mensajeError);
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  };

  enviarRef.current = enviar;

  const cancelarBorradorWhatsApp = async () => {
    if (!clinicId || !conversacionActiva || !pendingWhatsAppDraft || enviando) return;
    setError("");
    try {
      const resultado = await llamarVexaDentAI({
        clinicId,
        conversationId: conversacionActiva,
        draftId: pendingWhatsAppDraft.id,
        operation: "cancel_whatsapp_draft",
      });
      if (resultado?.data?.cancelled) setPendingWhatsAppDraft(null);
      else setError("El borrador ya no está pendiente para cancelar.");
    } catch (err) {
      console.error("Error al cancelar el borrador de WhatsApp:", err);
      try {
        const estado = await llamarVexaDentAI({
          clinicId,
          operation: "load_conversation",
          conversationId: conversacionActiva,
        });
        setPendingWhatsAppDraft(estado?.data?.pendingWhatsAppDraft || null);
      } catch (refreshError) {
        console.error("Error al actualizar el borrador de WhatsApp:", refreshError);
      }
      setError(err?.message || "No se pudo cancelar el borrador de WhatsApp.");
    }
  };

  const programarEscuchaVoz = () => {
    window.clearTimeout(voiceRestartTimeoutRef.current);
    voiceRestartTimeoutRef.current = window.setTimeout(() => {
      iniciarEscuchaVozRef.current?.();
    }, 350);
  };

  const procesarTranscripcionVoz = (transcripcion) => {
    const texto = String(transcripcion || "").trim();
    const activador = texto.match(/^\s*(?:oye\s+)?dent\b[\s,.:;!?¡¿-]*/i);
    let comando = "";

    if (activador) {
      comando = texto.slice(activador[0].length).trim();
      if (!comando) {
        voiceCommandArmedRef.current = true;
        setVoiceStatus("Dent activado. Di tu instrucción ahora.");
        window.clearTimeout(voiceArmTimeoutRef.current);
        voiceArmTimeoutRef.current = window.setTimeout(() => {
          voiceCommandArmedRef.current = false;
          setVoiceStatus('Escuchando. Di “Dent” para activar un comando.');
          programarEscuchaVoz();
        }, 8000);
        programarEscuchaVoz();
        return;
      }
      voiceCommandArmedRef.current = false;
      window.clearTimeout(voiceArmTimeoutRef.current);
    } else if (voiceCommandArmedRef.current) {
      comando = texto;
      voiceCommandArmedRef.current = false;
      window.clearTimeout(voiceArmTimeoutRef.current);
    } else {
      setVoiceStatus('Escuchando. Di “Dent” para activar un comando.');
      programarEscuchaVoz();
      return;
    }

    if (!comando) {
      programarEscuchaVoz();
      return;
    }

    setVoiceTranscript("");
    setVoiceStatus("Procesando tu comando…");
    setMensaje(comando);
    enviarRef.current?.(comando, { voiceInput: true });
  };

  const iniciarEscuchaVoz = () => {
    if (
      !voiceModeRef.current ||
      !voiceLocalReadyRef.current ||
      voiceRecognitionRef.current ||
      voiceStartingRef.current ||
      voiceSpeakingRef.current ||
      enviandoRef.current
    ) return;

    const SpeechRecognitionConstructor =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionConstructor) return;

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = voiceRecognitionLanguageRef.current;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.processLocally = true;
    voiceTranscriptRef.current = "";
    voiceRecognitionRef.current = recognition;
    voiceStartingRef.current = true;

    recognition.onstart = () => {
      voiceStartingRef.current = false;
      setVoiceStatus(voiceCommandArmedRef.current
        ? "Dent activado. Di tu instrucción ahora."
        : 'Escuchando. Di “Dent” para activar un comando.');
    };

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript || "";
        if (result.isFinal) finalText += `${transcript} `;
        else interimText += `${transcript} `;
      }
      if (finalText) {
        voiceTranscriptRef.current = `${voiceTranscriptRef.current} ${finalText}`.trim();
      }
      setVoiceTranscript(`${voiceTranscriptRef.current} ${interimText}`.trim());
    };

    recognition.onerror = (event) => {
      if (["no-speech", "aborted"].includes(event.error)) return;
      const explanation = event.error === "not-allowed" || event.error === "service-not-allowed"
        ? "Permite el acceso al micrófono en el navegador para usar comandos de voz."
        : event.error === "language-not-supported"
          ? "El reconocimiento local en español no está disponible en este navegador."
          : "No se pudo iniciar el micrófono. Revisa el dispositivo y los permisos del navegador.";
      setVoiceError(explanation);
      setVoiceStatus("");
      voiceModeRef.current = false;
      voiceLocalReadyRef.current = false;
      voiceCommandArmedRef.current = false;
      window.clearTimeout(voiceArmTimeoutRef.current);
      setVoiceMode(false);
    };

    recognition.onend = () => {
      if (voiceRecognitionRef.current === recognition) {
        voiceRecognitionRef.current = null;
      }
      voiceStartingRef.current = false;
      const transcript = voiceTranscriptRef.current.trim();
      voiceTranscriptRef.current = "";

      if (!voiceModeRef.current || voiceSpeakingRef.current || enviandoRef.current) return;
      if (transcript) {
        procesarTranscripcionVoz(transcript);
      } else {
        programarEscuchaVoz();
      }
    };

    try {
      recognition.start();
    } catch (error) {
      voiceRecognitionRef.current = null;
      voiceStartingRef.current = false;
      setVoiceError(error?.message || "No se pudo iniciar el reconocimiento de voz.");
      voiceModeRef.current = false;
      voiceLocalReadyRef.current = false;
      voiceCommandArmedRef.current = false;
      setVoiceMode(false);
    }
  };
  iniciarEscuchaVozRef.current = iniciarEscuchaVoz;

  const hablarRespuesta = (texto) => {
    if (!voiceModeRef.current) return;
    const SpeechSynthesis = window.speechSynthesis;
    const Utterance = window.SpeechSynthesisUtterance;
    if (!SpeechSynthesis || !Utterance) {
      setVoiceError("Este navegador no permite leer en voz alta las respuestas.");
      setVoiceStatus('Escuchando. Di “Dent” para activar un comando.');
      programarEscuchaVoz();
      return;
    }

    const lectura = String(texto || "")
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/^#{1,3}\s+/gm, "")
      .replace(/\*\*|\*|`/g, "")
      .replace(/^\s*[-•]\s+/gm, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!lectura) {
      programarEscuchaVoz();
      return;
    }

    const generation = voiceGenerationRef.current;
    const utterance = new Utterance(lectura);
    utterance.lang = "es-MX";
    utterance.rate = 1;
    voiceSpeakingRef.current = true;
    setVoiceTranscript("");
    setVoiceStatus("VexaDent AI está respondiendo en voz…");
    SpeechSynthesis.cancel();
    utterance.onend = () => {
      if (generation !== voiceGenerationRef.current) return;
      voiceSpeakingRef.current = false;
      if (!voiceModeRef.current) return;
      setVoiceStatus('Escuchando. Di “Dent” para activar un comando.');
      programarEscuchaVoz();
    };
    utterance.onerror = () => {
      if (generation !== voiceGenerationRef.current) return;
      voiceSpeakingRef.current = false;
      if (!voiceModeRef.current) return;
      setVoiceStatus('Escuchando. Di “Dent” para activar un comando.');
      programarEscuchaVoz();
    };
    SpeechSynthesis.speak(utterance);
  };
  hablarRespuestaRef.current = hablarRespuesta;

  const alternarVoz = async () => {
    if (voicePreparing) return;
    if (voiceModeRef.current) {
      voiceGenerationRef.current += 1;
      voiceModeRef.current = false;
      voiceLocalReadyRef.current = false;
      voiceCommandArmedRef.current = false;
      voiceSpeakingRef.current = false;
      window.clearTimeout(voiceArmTimeoutRef.current);
      window.clearTimeout(voiceRestartTimeoutRef.current);
      voiceRecognitionRef.current?.stop();
      voiceRecognitionRef.current = null;
      window.speechSynthesis?.cancel();
      setVoiceMode(false);
      setVoiceStatus("");
      setVoiceTranscript("");
      setVoiceError("");
      return;
    }

    setVoiceError("");
    setVoiceTranscript("");
    setVoicePreparing(true);
    setVoiceStatus(voiceLanguagePackReadyRef.current
      ? "Activando reconocimiento local en español…"
      : "Preparando reconocimiento local en español…");
    try {
      const SpeechRecognitionConstructor =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognitionConstructor) {
        throw new Error("Este navegador no ofrece reconocimiento de voz para VexaDent AI.");
      }
      if (!voiceLanguagePackReadyRef.current) {
        const probe = new SpeechRecognitionConstructor();
        if (
          !("processLocally" in probe) ||
          typeof SpeechRecognitionConstructor.available !== "function" ||
          typeof SpeechRecognitionConstructor.install !== "function"
        ) {
          throw new Error("Este navegador no permite reconocimiento local de voz. Para proteger la privacidad clínica, el audio no se enviará a un servicio externo.");
        }

        const profiles = [
          {language: "es-MX", quality: "dictation"},
          {language: "es-ES", quality: "dictation"},
          {language: "es-MX", quality: "command"},
          {language: "es-ES", quality: "command"},
        ];
        let selectedProfile = null;
        for (const profile of profiles) {
          const options = {
            langs: [profile.language],
            processLocally: true,
            quality: profile.quality,
          };
          const availability = await SpeechRecognitionConstructor.available(options);
          if (availability === "available") {
            selectedProfile = profile;
            break;
          }
          if (availability === "downloadable" || availability === "downloading") {
            setVoiceStatus(`Instalando el paquete local ${profile.language}…`);
            const installed = await SpeechRecognitionConstructor.install(options);
            if (installed) {
              selectedProfile = profile;
              break;
            }
          }
        }
        if (!selectedProfile) {
          throw new Error("Este dispositivo no ofrece paquetes locales compatibles de español para voz.");
        }
        voiceRecognitionLanguageRef.current = selectedProfile.language;
        voiceLanguagePackReadyRef.current = true;
        setVoiceStatus(`Paquete local ${selectedProfile.language} listo. Pulsa otra vez para activar el micrófono.`);
        return;
      }

      voiceGenerationRef.current += 1;
      voiceCommandArmedRef.current = false;
      voiceLocalReadyRef.current = true;
      voiceModeRef.current = true;
      setVoiceMode(true);
      setVoiceStatus('Micrófono activo. Di “Dent” y luego tu comando.');
      iniciarEscuchaVozRef.current?.();
    } catch (error) {
      setVoiceError(error?.message || "No se pudo preparar el reconocimiento local de voz.");
      setVoiceStatus("");
    } finally {
      setVoicePreparing(false);
    }
  };

  const renderPanelHistorial = (panelMovil = false) => (
    <aside
      className="vd-ai-history"
      style={{
        ...styles.historySidebar,
        ...(panelMovil ? styles.mobileHistoryPanel : {}),
      }}
      aria-label="Historial de conversaciones"
      onClick={(event) => event.stopPropagation()}
    >
      <div style={styles.historyHeader}>
        <span>Historial</span>
        {panelMovil && (
          <button
            type="button"
            onClick={() => setHistorialMovilAbierto(false)}
            aria-label="Cerrar historial"
            style={styles.closeHistory}
          >
            <X size={18} />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={iniciarNuevaConversacion}
        disabled={enviando}
        style={styles.newConversation}
      >
        <Plus size={17} />
        <span>Nueva conversación</span>
      </button>

      <div style={styles.historyList}>
        {historialCargando && (
          <p style={styles.historyNotice}>Cargando conversaciones…</p>
        )}
        {!historialCargando && errorHistorial && (
          <p style={styles.historyNotice}>{errorHistorial}</p>
        )}
        {!historialCargando && !errorHistorial && conversaciones.length === 0 && (
          <p style={styles.historyNotice}>Tus conversaciones aparecerán aquí.</p>
        )}
        {conversaciones.map((conversacion) => (
          <div key={conversacion.id} style={styles.historyRow}>
            <button
              type="button"
              onClick={() => abrirConversacion(conversacion.id)}
              disabled={enviando || Boolean(conversacionCargando) || conversacion.deleting}
              aria-current={conversacion.id === conversacionActiva ? "page" : undefined}
              className="vd-ai-history-item"
              style={{
                ...styles.historyItem,
                ...(conversacion.id === conversacionActiva ? styles.historyItemActive : {}),
                opacity: conversacionCargando && conversacionCargando !== conversacion.id ? 0.6 : 1,
              }}
            >
              <MessageSquare size={16} />
              <span style={styles.historyItemText}>
                <span style={styles.historyItemTitle}>
                  {conversacionCargando === conversacion.id
                    ? "Abriendo…"
                    : conversacion.deleting
                      ? `Eliminación pendiente: ${conversacion.title || "Nueva conversación"}`
                      : conversacion.title || "Nueva conversación"}
                </span>
                {conversacion.preview && (
                  <span style={styles.historyItemPreview}>
                    {conversacion.preview}
                  </span>
                )}
              </span>
            </button>
            <button
              type="button"
              aria-label={`Eliminar conversación ${conversacion.title || "Nueva conversación"}`}
              title="Eliminar conversación"
              onClick={(event) => {
                event.stopPropagation();
                if (!enviando && !conversacionCargando && !deletingConversationId) {
                  setConversationToDelete(conversacion);
                }
              }}
              disabled={enviando || Boolean(conversacionCargando) || Boolean(deletingConversationId)}
              style={{
                ...styles.historyDeleteButton,
                opacity: deletingConversationId === conversacion.id ? 0.5 : 1,
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </aside>
  );

  return (
    <main
      className="vd-module-page vd-ai-page"
      style={{
        ...styles.page,
        ...(mobile ? styles.mobilePage : {}),
      }}
    >
      <header className="vd-ai-header" style={styles.header}>
        <div style={styles.brandIcon}>
          <Sparkles size={23} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={styles.title}>VexaDent AI</h1>
          <p style={styles.subtitle}>Tu asistente inteligente</p>
        </div>

        {mobile ? (
          <button
            type="button"
            onClick={() => setHistorialMovilAbierto(true)}
            style={styles.mobileHistoryToggle}
          >
            <MessageSquare size={16} />
            <span>Historial</span>
          </button>
        ) : (
          <div style={styles.secure}>
            <ShieldCheck size={16} />
            <span>Asistente de VexaDent</span>
          </div>
        )}
      </header>

      <div className="vd-ai-workspace" style={styles.workspace}>
        {!mobile && renderPanelHistorial()}

        <div className="vd-ai-chat-column" style={styles.chatColumn}>
          <section
            className="vd-ai-chat"
            ref={chatRef}
            style={styles.chat}
            aria-label="Conversación con VexaDent AI"
          >
        {mensajes.length === 0 ? (
          <div style={styles.welcome}>
            <div style={styles.welcomeIcon}>
              <Sparkles size={30} />
            </div>

            <h2 style={styles.heading}>
              ¿En qué te puedo ayudar?
            </h2>

            <p style={styles.intro}>
              Pregunta sobre odontología, administración de tu
              consultorio o tecnología. También puedo consultar pacientes,
              agendar y actualizar citas, registrar pagos y guardar
              evoluciones clínicas.
            </p>

            <div style={styles.suggestions}>
              {sugerencias.map(({ icon: Icon, texto }) => (
                <button
                  key={texto}
                  type="button"
                  className="vd-ai-suggestion"
                  onClick={() => setMensaje(texto)}
                  style={styles.suggestion}
                  disabled={enviando}
                >
                  <Icon size={18} />
                  <span>{texto}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.messages}>
            {mensajes.map((item) => (
              <div
                key={item.id}
                style={{
                  ...styles.messageRow,
                  justifyContent:
                    item.role === "user"
                      ? "flex-end"
                      : "flex-start",
                }}
              >
                {item.role === "assistant" && (
                  <div style={styles.avatar}>
                    <Bot size={18} />
                  </div>
                )}

                <div
                  style={{
                    ...styles.bubble,
                    ...(item.role === "user"
                      ? styles.userBubble
                      : styles.aiBubble),
                  }}
                >
                  {item.role === "assistant" ? (
                    <ContenidoMarkdown contenido={item.content} />
                  ) : (
                    item.content
                  )}
                </div>

                {item.role === "user" && (
                  <div
                    style={{
                      ...styles.avatar,
                      ...styles.userAvatar,
                    }}
                  >
                    <UserRound size={18} />
                  </div>
                )}
              </div>
            ))}

            {enviando && (
              <div style={styles.messageRow}>
                <div style={styles.avatar}>
                  <Bot size={18} />
                </div>

                <div
                  style={{
                    ...styles.bubble,
                    ...styles.aiBubble,
                  }}
                >
                  <span style={styles.loadingText}>
                    VexaDent AI está escribiendo...
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div role="alert" style={styles.error}>
            {error}
          </div>
        )}

        {pendingWhatsAppDraft && (
          <div style={styles.whatsappDraftCard}>
            <div style={styles.whatsappDraftTitle}>
              <MessageSquare size={18} />
              <strong>{pendingWhatsAppDraft.kind === "payment_receipt" ? "¿Enviar el recibo por WhatsApp?" : "Revisa el mensaje de WhatsApp"}</strong>
            </div>
            <p style={styles.whatsappDraftRecipient}>
              <strong>Para:</strong> {pendingWhatsAppDraft.patientName} · {pendingWhatsAppDraft.phone}
            </p>
            <div style={styles.whatsappDraftMessage}>{pendingWhatsAppDraft.message}</div>
            {pendingWhatsAppDraft.kind === "payment_receipt" && pendingWhatsAppDraft.fileUrl && (
              <a href={pendingWhatsAppDraft.fileUrl} target="_blank" rel="noopener noreferrer">
                Ver recibo PDF
              </a>
            )}
            <p style={styles.whatsappDraftHint}>
              Solo se enviará después de confirmar. El borrador vence en 10 minutos.
            </p>
            <div style={styles.whatsappDraftActions}>
              <button
                type="button"
                onClick={() => enviar("Confirmo el envío por WhatsApp")}
                disabled={enviando}
                style={{ ...styles.whatsappConfirm, opacity: enviando ? 0.6 : 1 }}
              >
                <Check size={16} />
                {pendingWhatsAppDraft.kind === "payment_receipt" ? "Sí, enviar recibo" : "Confirmar envío"}
              </button>
              <button
                type="button"
                onClick={pendingWhatsAppDraft.kind === "payment_receipt" ? () => enviar("No enviar el recibo") : cancelarBorradorWhatsApp}
                disabled={enviando}
                style={styles.whatsappCancel}
              >
                <X size={16} />
                {pendingWhatsAppDraft.kind === "payment_receipt" ? "No enviar" : "Cancelar"}
              </button>
            </div>
          </div>
        )}
          </section>

          <form
            className="vd-ai-composer"
            style={styles.composer}
            onSubmit={(event) => {
              event.preventDefault();
              enviar();
            }}
          >
        <div
          role={voiceError ? "alert" : "status"}
          aria-live="polite"
          style={{
            ...styles.voiceGuide,
            ...(voiceError ? styles.voiceGuideError : {}),
          }}
        >
          {voiceError || voiceTranscript || voiceStatus || (voiceMode
            ? 'Micrófono activo. Di “Dent” y luego tu comando.'
            : 'Activa el micrófono y di “Dent” para llamar a VexaDent AI.')}
        </div>
        <textarea
          aria-label="Escribe tu pregunta"
          value={mensaje}
          onChange={(event) => setMensaje(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              enviar();
            }
          }}
          placeholder="Escribe tu pregunta aquí..."
          rows={2}
          maxLength={6000}
          style={styles.textarea}
          disabled={enviando}
        />

        <button
          type="submit"
          disabled={!mensaje.trim() || enviando}
          aria-label="Enviar mensaje"
          style={{
            ...styles.send,
            opacity:
              mensaje.trim() && !enviando ? 1 : 0.45,
            cursor:
              mensaje.trim() && !enviando
                ? "pointer"
                : "not-allowed",
          }}
        >
          <Send size={19} />
        </button>

        <button
          type="button"
          onClick={alternarVoz}
          disabled={voicePreparing}
          aria-label={voiceMode ? "Desactivar comandos de voz" : "Activar micrófono y decir Dent"}
          aria-pressed={voiceMode}
          title={voiceMode ? "Desactivar micrófono" : 'Activa el micrófono y di “Dent”'}
          style={{
            ...styles.voiceButton,
            ...(voiceMode ? styles.voiceButtonActive : {}),
            opacity: voicePreparing ? 0.65 : 1,
          }}
        >
          {voiceMode ? <MicOff size={18} /> : <Mic size={18} />}
        </button>

        <p style={styles.disclaimer}>
          {enviando
            ? "Conectando con VexaDent AI..."
            : "VexaDent AI"}
        </p>
          </form>
        </div>
      </div>

      {mobile && historialMovilAbierto && (
        <div
          role="presentation"
          style={styles.historyOverlay}
          onClick={() => setHistorialMovilAbierto(false)}
        >
          {renderPanelHistorial(true)}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(conversationToDelete)}
        title="Eliminar conversación"
        message={`Se eliminará “${conversationToDelete?.title || "Nueva conversación"}”, incluidos sus mensajes y borradores pendientes de WhatsApp. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Conservar"
        type="danger"
        loading={deletingConversationId === conversationToDelete?.id}
        closeOnOverlay={!deletingConversationId}
        onCancel={() => {
          if (!deletingConversationId) setConversationToDelete(null);
        }}
        onConfirm={eliminarConversacion}
      />
    </main>
  );
}

const styles = {
  page: {
    minHeight: 0,
    height: "calc(100vh - 40px)",
    display: "flex",
    flexDirection: "column",
    background: "#f8fafc",
    color: "#0f172a",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  mobilePage: {
    height: "calc(100dvh - 158px)",
  },

  workspace: {
    display: "flex",
    flex: "1 1 0%",
    minHeight: 0,
    overflow: "hidden",
  },

  chatColumn: {
    display: "flex",
    flex: "1 1 0%",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
  },

  historySidebar: {
    display: "flex",
    flex: "0 0 260px",
    flexDirection: "column",
    width: 260,
    minWidth: 0,
    minHeight: 0,
    padding: 16,
    boxSizing: "border-box",
    borderRight: "1px solid #e2e8f0",
    background: "#f1f5f9",
  },

  historyHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 34,
    marginBottom: 12,
    padding: "0 4px",
    color: "#475569",
    fontSize: 13,
    fontWeight: 700,
  },

  newConversation: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    minHeight: 42,
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: 11,
    background: "#fff",
    color: "#1e293b",
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },

  historyList: {
    display: "flex",
    flex: "1 1 0%",
    flexDirection: "column",
    gap: 5,
    minHeight: 0,
    marginTop: 14,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  },

  historyNotice: {
    margin: "8px 5px",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.5,
  },

  historyRow: {
    display: "flex",
    alignItems: "stretch",
    gap: 3,
    width: "100%",
  },

  historyItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    flex: "1 1 0%",
    width: "auto",
    minWidth: 0,
    padding: "10px 9px",
    border: "1px solid transparent",
    borderRadius: 10,
    background: "transparent",
    color: "#475569",
    textAlign: "left",
    font: "inherit",
    cursor: "pointer",
  },

  historyDeleteButton: {
    display: "flex",
    flex: "0 0 34px",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    minHeight: 34,
    alignSelf: "flex-start",
    padding: 0,
    border: "1px solid transparent",
    borderRadius: 9,
    background: "transparent",
    color: "#94a3b8",
    cursor: "pointer",
  },

  historyItemActive: {
    borderColor: "#dbeafe",
    background: "#fff",
    color: "#1d4ed8",
  },

  historyItemText: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },

  historyItemTitle: {
    overflow: "hidden",
    fontSize: 12,
    fontWeight: 600,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  historyItemPreview: {
    display: "-webkit-box",
    overflow: "hidden",
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.35,
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
  },

  mobileHistoryToggle: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: 6,
    padding: "8px 10px",
    border: "1px solid #dbe3ef",
    borderRadius: 10,
    background: "#fff",
    color: "#475569",
    font: "inherit",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },

  historyOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1500,
    display: "flex",
    background: "rgba(15,23,42,.4)",
  },

  mobileHistoryPanel: {
    flex: "0 0 auto",
    width: "min(300px, 86vw)",
    height: "100%",
    borderRight: "1px solid #e2e8f0",
    boxShadow: "8px 0 24px rgba(15,23,42,.14)",
  },

  closeHistory: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    border: 0,
    borderRadius: 8,
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    padding: "22px 26px",
    background: "#fff",
    borderBottom: "1px solid #e8edf4",
    position: "sticky",
    top: 0,
    zIndex: 2,
    flexShrink: 0,
  },

  brandIcon: {
    width: 46,
    height: 46,
    flexShrink: 0,
    borderRadius: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    boxShadow: "0 6px 16px rgba(37,99,235,.22)",
  },

  title: {
    fontSize: 20,
    fontWeight: 750,
    margin: 0,
    letterSpacing: "-0.4px",
  },

  subtitle: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: 13,
  },

  secure: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#64748b",
    fontSize: 12,
    whiteSpace: "nowrap",
  },

  chat: {
    flex: "1 1 0%",
    minHeight: 0,
    overflowY: "auto",
    padding: "28px 20px 18px",
    boxSizing: "border-box",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  },

  welcome: {
    width: "100%",
    maxWidth: 720,
    margin: "42px auto 24px",
    textAlign: "center",
  },

  welcomeIcon: {
    width: 62,
    height: 62,
    borderRadius: 21,
    margin: "0 auto 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2563eb",
    background: "#eaf1ff",
  },

  heading: {
    fontSize: 27,
    letterSpacing: "-0.7px",
    margin: "0 0 10px",
    fontWeight: 750,
  },

  intro: {
    fontSize: 15,
    lineHeight: 1.65,
    color: "#64748b",
    maxWidth: 520,
    margin: "0 auto",
  },

  suggestions: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit,minmax(190px,1fr))",
    gap: 10,
    marginTop: 28,
    textAlign: "left",
  },

  suggestion: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 64,
    padding: "13px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 15,
    background: "#fff",
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.4,
    cursor: "pointer",
    textAlign: "left",
  },

  messages: {
    maxWidth: 850,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  messageRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },

  avatar: {
    width: 34,
    height: 34,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "#2563eb",
    background: "#eaf1ff",
  },

  userAvatar: {
    color: "#475569",
    background: "#e2e8f0",
  },

  bubble: {
    maxWidth: "min(78%,680px)",
    padding: "13px 15px",
    borderRadius: 16,
    fontSize: 14,
    lineHeight: 1.65,
    overflowWrap: "anywhere",
  },

  userBubble: {
    background: "#2563eb",
    color: "#fff",
    borderTopRightRadius: 5,
    whiteSpace: "pre-wrap",
  },

  aiBubble: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    color: "#334155",
    borderTopLeftRadius: 5,
  },

  markdownContent: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  markdownParagraph: {
    margin: "0 0 7px",
    whiteSpace: "pre-wrap",
  },

  markdownList: {
    margin: "0 0 10px",
    paddingLeft: 23,
  },

  markdownHeading: {
    fontSize: 18,
    fontWeight: 750,
    lineHeight: 1.35,
    margin: "8px 0 5px",
    color: "#0f172a",
  },

  markdownSubheading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.4,
    margin: "7px 0 4px",
    color: "#0f172a",
  },

  markdownSmallHeading: {
    fontSize: 14,
    fontWeight: 700,
    margin: "5px 0 3px",
    color: "#0f172a",
  },

  inlineCode: {
    fontFamily: "monospace",
    fontSize: "0.92em",
    padding: "2px 5px",
    borderRadius: 5,
    background: "#f1f5f9",
    color: "#334155",
  },

  composer: {
    position: "relative",
    flexShrink: 0,
    width: "calc(100% - 40px)",
    maxWidth: 850,
    margin: "0 auto 22px",
    padding: 12,
    paddingBottom: 34,
    border: "1px solid #dbe3ef",
    borderRadius: 20,
    background: "#fff",
    boxShadow: "0 8px 28px rgba(15,23,42,.06)",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    display: "block",
    resize: "vertical",
    minHeight: 48,
    maxHeight: 180,
    border: 0,
    outline: 0,
    background: "transparent",
    color: "#0f172a",
    font: "inherit",
    fontSize: 14,
    lineHeight: 1.6,
    padding: "4px 100px 4px 5px",
    boxSizing: "border-box",
  },

  send: {
    position: "absolute",
    right: 12,
    top: 12,
    width: 40,
    height: 40,
    border: 0,
    borderRadius: 13,
    background: "#2563eb",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  voiceButton: {
    position: "absolute",
    right: 60,
    top: 12,
    width: 40,
    height: 40,
    border: "1px solid #dbe3ef",
    borderRadius: 13,
    background: "#f8fafc",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  voiceButtonActive: {
    borderColor: "#fecaca",
    background: "#fef2f2",
    color: "#dc2626",
  },

  voiceGuide: {
    display: "flex",
    alignItems: "center",
    minHeight: 21,
    margin: "0 52px 4px 5px",
    color: "#64748b",
    fontSize: 11,
    lineHeight: 1.35,
  },

  voiceGuideError: {
    color: "#b91c1c",
  },

  error: {
    maxWidth: 850,
    margin: "12px auto 0",
    padding: "12px 15px",
    borderRadius: 12,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: 13,
    lineHeight: 1.5,
  },

  whatsappDraftCard: {
    width: "calc(100% - 40px)",
    maxWidth: 850,
    boxSizing: "border-box",
    margin: "14px auto 18px",
    padding: 16,
    border: "1px solid #bfdbfe",
    borderRadius: 16,
    background: "#eff6ff",
    color: "#1e3a8a",
  },

  whatsappDraftTitle: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    fontSize: 14,
  },

  whatsappDraftRecipient: {
    margin: "12px 0 8px",
    fontSize: 13,
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },

  whatsappDraftMessage: {
    padding: 12,
    border: "1px solid #dbeafe",
    borderRadius: 11,
    background: "#fff",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  whatsappDraftHint: {
    margin: "9px 0 12px",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.5,
  },

  whatsappDraftActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 9,
  },

  whatsappConfirm: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    minHeight: 38,
    padding: "0 13px",
    border: 0,
    borderRadius: 10,
    background: "#2563eb",
    color: "#fff",
    font: "inherit",
    fontSize: 13,
    fontWeight: 650,
    cursor: "pointer",
  },

  whatsappCancel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    minHeight: 38,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "#fff",
    color: "#334155",
    font: "inherit",
    fontSize: 13,
    cursor: "pointer",
  },

  loadingText: {
    color: "#64748b",
    fontStyle: "italic",
  },

  disclaimer: {
    position: "absolute",
    left: 17,
    bottom: 9,
    margin: 0,
    fontSize: 10,
    color: "#94a3b8",
  },
};
